import React, { useMemo } from "react";
import { Box, Paper, Typography } from "@mui/material";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

// Match your table formatting (UTC date bucketing)
const toYMD_UTC = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const prettyLabel = (ymd) => {
  const d = new Date(`${ymd}T00:00:00Z`);
  return d.toLocaleDateString(undefined, { month: "short", day: "2-digit" });
};

const CustomerDashCallChart = ({ rows, title = "Calls", label = "Calls", isCurrency = true }) => {
  const { labels, values } = useMemo(() => {
    const map = new Map(); // ymd -> total payment amount

    (rows || []).forEach((r) => {
      const key = toYMD_UTC(r?.created_date);
      if (!key) return;

      const amt = Number(r?.records_count) || 0;
      map.set(key, (map.get(key) || 0) + amt);
    });

    const keys = [...map.keys()].sort();

    return {
      labels: keys.map(prettyLabel),
      values: keys.map((k) => map.get(k)),
    };
  }, [rows]);

  if (!rows?.length) return null;

  const data = {
    labels,
    datasets: [
      {
        label: label,
        data: values,
        backgroundColor: "#DCEAF7",
        fill: true,

        // No lines / no dots
        borderWidth: 0,
        pointRadius: 0,
        pointHoverRadius: 0,

        // harmless even though no border
        tension: 0.35,
      },
    ],
  };

  const formatValue = (value) => {
    if (isCurrency) {
      return Number(value).toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
      });
    }
    return Number(value).toLocaleString("en-US");
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => ` ${formatValue(ctx.parsed.y)}`,
        },
      },
    },
    elements: {
      line: { borderWidth: 0 },
      point: { radius: 0 },
    },
    scales: {
      x: {
        grid: { display: false },
      },
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value) => formatValue(value),
        },
      },
    },
  };

  return (
    <Paper sx={{ p: 2, mb: 2, width: "97.5%" }}>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        {title}
      </Typography>
      <Box sx={{ height: 260 }}>
        <Line data={data} options={options} />
      </Box>
    </Paper>
  );
};

export default CustomerDashCallChart;
